import type { EmbedToken } from '@paladigm/answer-visuals';

import { refetchToken } from '@shared/auth/auth';
import { tokenStore } from '@shared/auth/token.store';
import { fileExtension, mimeTypeForExtension } from '@shared/lib/file-format';

import { isDefaultModel } from '../data/models';
import type { Level, ModelThinking } from '../data/thinking-levels';
import type { UploadOptions } from '../types';

import { readAttachResponse, readUploadRefusal } from './wire';

// ── Config ───────────────────────────────────────────────────────────────────
// Same-origin proxy prefix for the IQ AI CIO orchestrator. The browser hits
// `/ai-cio/api/v1/...` on the CRM origin; nginx (prod) / the Vite dev proxy
// strips `/ai-cio` and forwards to the orchestrator so it sees its native
// `/api/v1/...` paths. This avoids Chrome's Local Network Access block on
// public→private fetches and keeps the orchestrator off the CRM `/api`
// namespace. Override the prefix with VITE_AI_CIO_API_URL if ever needed.

const BASE_URL = (import.meta.env.VITE_AI_CIO_API_URL ?? '/ai-cio').replace(/\/$/, '');
const API_PREFIX = '/api/v1';

function url(path: string): string {
  return `${BASE_URL}${API_PREFIX}${path}`;
}

/**
 * Auth headers for the orchestrator. The CRM is the `crm` platform, so we send
 * `X-Platform: crm` plus the CRM access token as a bearer (FRONTEND_INTEGRATION
 * §2). The token must be present — callers run inside the authenticated shell.
 */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'X-Platform': 'crm' };
  const token = tokenStore.getState().accessToken?.token;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * `fetch` plus the orchestrator auth headers, with the one thing this client
 * was missing: recovery from an expired CRM access token.
 *
 * The GraphQL layer already does this (urql authExchange -> didAuthError /
 * refreshAuth, shared/api/exchanges/auth.exchange.ts). Without it here, a stale
 * token left AI CIO 401ing while the rest of the CRM silently refreshed and
 * kept working, so the panel stayed dead until a page reload and the failure
 * looked random. Observed on stage 2026-08-04 07:22 UTC:
 * POST /sessions/<id>/messages -> 401, then a fresh session -> 200.
 *
 * One refresh, one retry. `refetchToken()` is ALREADY de-duplicated across
 * concurrent callers and redirects to /login itself on a definitive 401/403 --
 * so this must not add a stampede guard or a redirect of its own.
 *
 * The retry re-evaluates `authHeaders()`, so attempt two carries the NEW token;
 * calling it once outside `send` would replay the stale one. Bodies here are
 * JSON strings or FormData, both replayable -- a streaming request body would
 * not be, so revisit this if one is ever added.
 */
async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const send = (): Promise<Response> =>
    fetch(input, {
      ...init,
      headers: { ...authHeaders(), ...(init.headers as Record<string, string> | undefined) },
    });

  const res = await send();
  if (res.status !== 401) return res;

  try {
    await refetchToken();
  } catch {
    // The refresh did not yield a token -- a definitive 401/403 (the redirect to
    // /login is already under way) or a network/5xx failure. Either way surface
    // the original 401 with its body unread so ensureOk can still report the
    // orchestrator's detail.
    return res;
  }

  void res.body?.cancel();
  return send();
}

// ── SSE wire types ───────────────────────────────────────────────────────────

export interface SseEvent {
  event: string;
  data: Record<string, unknown>;
}

/**
 * Parses a POST `text/event-stream` body into discrete events. Native
 * EventSource only supports GET, so we read the stream manually
 * (FRONTEND_INTEGRATION §4.6).
 */
async function* readSse(response: Response, signal?: AbortSignal): AsyncGenerator<SseEvent> {
  const reader = response.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';
      for (const frame of frames) {
        if (!frame.trim() || frame.startsWith(':')) continue; // keepalive/comment
        const eventName = /^event:\s*(.+)$/m.exec(frame)?.[1]?.trim();
        const dataLine = /^data:\s*(.+)$/m.exec(frame)?.[1]?.trim();
        if (!eventName || !dataLine) continue;
        try {
          yield { event: eventName, data: JSON.parse(dataLine) as Record<string, unknown> };
        } catch {
          // Skip malformed frames rather than tearing down the whole stream.
        }
      }
    }
  } finally {
    // cancel() releases the lock AND closes the underlying stream, so it's
    // safe whether the consumer drained the stream or broke out early (e.g.
    // createSession returning as soon as it sees SESSION_STARTED). Ignore the
    // rejection if the stream is already closed.
    await reader.cancel().catch(() => undefined);
  }
}

async function ensureOk(response: Response): Promise<Response> {
  if (response.ok) return response;
  throw await refusalDetail(response, response.status.toString());
}

export class OrchestratorError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly limitBytes?: number,
  ) {
    super(message);
    this.name = 'OrchestratorError';
  }
}

// ── Model selection ──────────────────────────────────────────────────────────
// Send an optional `model` id per request (mirrors `language`); the backend
// resolves it and falls back to the server default on unknown/absent. The
// control is driven entirely by `GET /api/v1/models`: id, provider, display
// label/description, the group it is listed under, the marks it shows and each
// model's thinking levels all come from the backend (single source of truth).

/** The group a model is listed under in the control (`GET /api/v1/models` → `group`). */
export interface ModelGroup {
  id: string;
  label: string;
  /**
   * The id of its provider's mark (`components/ModelMark.tsx`); absent on the "Other" group and
   * from an orchestrator before marks.
   */
  icon?: string;
}

export interface ModelInfo {
  id: string;
  /** The provider; names the model's group when an older backend sends no `group`. */
  provider?: string;
  /** The group the control lists it under, by provider; absent on older backends. */
  group?: ModelGroup;
  /**
   * The id of its model line's mark (`components/ModelMark.tsx`); absent on the "Other" group and
   * from an orchestrator before marks.
   */
  icon?: string;
  /** Display copy from /models. Absent on older backends → fall back to the id. */
  label?: string;
  description?: string;
  /**
   * Its family's offered levels and the two defaults; null for a model that takes no level,
   * whose requests carry none. Absent from an orchestrator before `thinking_scale`.
   */
  thinking?: ModelThinking | null;
}

export interface ModelsResponse {
  models: ModelInfo[];
  /**
   * The model a turn runs on when the request names none; the control shows it by
   * name. Absent on older backends → the control names no model.
   */
  default?: string;
  /** The deployment's model as an entry, whether the list holds it or not. */
  default_model?: ModelInfo;
  /** True when the org pins a provider (B2B `llm_override`): the model panes are disabled. */
  override_active: boolean;
  /** The partner's model as an entry, while `override_active`. */
  override_model?: ModelInfo;
  /**
   * The shared scale's steps in rank order: the one key that says the orchestrator runs the
   * thinking and agentic contract (each model's own levels, `agentic`, the TURN_MODE note).
   */
  thinking_scale?: Level[];
}

const EMPTY_MODELS: ModelsResponse = { models: [], override_active: false };

/** A `/models` read waits this long: a cold worker's first one waits up to 5 s on the router. */
const MODELS_TIMEOUT_MS = 6000;

/** An `icon`: a non-empty string id, else none, which draws the group's monogram. */
function iconId(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * A `group` with a non-empty string id and label, else none: the provider names it then. Its
 * `icon` is kept when it is an id.
 */
function modelGroup(value: unknown): ModelGroup | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { id, label, icon } = value as { id?: unknown; label?: unknown; icon?: unknown };
  if (typeof id !== 'string' || !id || typeof label !== 'string' || !label) return undefined;
  const mark = iconId(icon);
  return mark ? { id, label, icon: mark } : { id, label };
}

/** A level or a scale step: a non-empty id and a finite rank; its label falls back to the id. */
function level(value: unknown): Level | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { id, label, rank } = value as { id?: unknown; label?: unknown; rank?: unknown };
  if (typeof id !== 'string' || !id || typeof rank !== 'number' || !Number.isFinite(rank)) {
    return undefined;
  }
  return { id, label: typeof label === 'string' && label ? label : id, rank };
}

function levels(value: unknown): Level[] {
  return Array.isArray(value)
    ? value.flatMap((v: unknown): Level[] => {
        const parsed = level(v);
        return parsed ? [parsed] : [];
      })
    : [];
}

/**
 * A model's `thinking`: undefined when absent (an older orchestrator), null for a model that
 * takes no level, and for one whose levels cannot be read, which then shows none.
 */
function thinking(value: unknown): ModelThinking | null | undefined {
  if (value === undefined) return undefined;
  if (!value || typeof value !== 'object') return null;
  const raw = value as {
    family?: unknown;
    levels?: unknown;
    default?: unknown;
    server_default?: unknown;
  };
  const offered = levels(raw.levels);
  const [first] = offered;
  if (!first) return null;
  const offeredId = (id: unknown): string | undefined =>
    typeof id === 'string' && offered.some((l) => l.id === id) ? id : undefined;
  return {
    family: typeof raw.family === 'string' ? raw.family : '',
    levels: offered,
    default: offeredId(raw.default) ?? first.id,
    server_default: offeredId(raw.server_default) ?? first.id,
  };
}

/** A model entry with an id (the id is all we send and match on), else none. */
function modelInfo(value: unknown): ModelInfo | undefined {
  const m = (value ?? {}) as {
    id?: unknown;
    provider?: unknown;
    label?: unknown;
    description?: unknown;
    group?: unknown;
    icon?: unknown;
    thinking?: unknown;
  };
  // Keep any model with an id; don't drop a usable model just because `provider` is absent.
  if (typeof m.id !== 'string') return undefined;
  const levelsOf = thinking(m.thinking);
  const mark = iconId(m.icon);
  return {
    id: m.id,
    provider: typeof m.provider === 'string' ? m.provider : undefined,
    label: typeof m.label === 'string' ? m.label : undefined,
    description: typeof m.description === 'string' ? m.description : undefined,
    group: modelGroup(m.group),
    ...(mark ? { icon: mark } : {}),
    ...(levelsOf !== undefined ? { thinking: levelsOf } : {}),
  };
}

/** A `/models` body, read defensively so a malformed one can't crash the control. */
function modelsResponse(body: unknown): ModelsResponse {
  const data = (body ?? {}) as {
    models?: unknown;
    default?: unknown;
    default_model?: unknown;
    override_active?: unknown;
    override_model?: unknown;
    thinking_scale?: unknown;
  };
  const models = Array.isArray(data.models)
    ? data.models.flatMap((m: unknown): ModelInfo[] => {
        const parsed = modelInfo(m);
        return parsed ? [parsed] : [];
      })
    : [];
  const defaultModel = modelInfo(data.default_model);
  const overrideModel = modelInfo(data.override_model);
  return {
    models,
    default: typeof data.default === 'string' && data.default ? data.default : undefined,
    ...(defaultModel ? { default_model: defaultModel } : {}),
    override_active: data.override_active === true,
    ...(overrideModel ? { override_model: overrideModel } : {}),
    // The key itself says the orchestrator runs the contract, so keep it only when sent.
    ...(Array.isArray(data.thinking_scale) ? { thinking_scale: levels(data.thinking_scale) } : {}),
  };
}

/** The response that answered 200 last; a failed or timed-out read is never held. */
let modelsPromise: Promise<ModelsResponse> | null = null;

const modelsListeners = new Set<(res: ModelsResponse) => void>();

/**
 * Called with every `/models` response that answered 200, so the control follows what a send
 * read. Returns the unsubscribe.
 */
export function subscribeModels(listener: (res: ModelsResponse) => void): () => void {
  modelsListeners.add(listener);
  return () => {
    modelsListeners.delete(listener);
  };
}

/** One `/models` read: its body on a 200, else null. */
async function readModels(): Promise<ModelsResponse | null> {
  try {
    const res = await authedFetch(url('/models'), {
      method: 'GET',
      signal: AbortSignal.timeout(MODELS_TIMEOUT_MS),
    });
    if (!res.ok) {
      if (import.meta.env.DEV) {
        console.warn(`[ai-cio] GET /models → ${String(res.status)}; the control has no models`);
      }
      return null;
    }
    const answered = modelsResponse(await res.json());
    for (const listener of modelsListeners) listener(answered);
    return answered;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[ai-cio] GET /models failed; the control has no models', err);
    }
    return null;
  }
}

export function fetchModelsStrict(): Promise<ModelsResponse> {
  return readModels().then((answered) => {
    if (!answered) return EMPTY_MODELS;
    modelsPromise = Promise.resolve(answered);
    return answered;
  });
}

/**
 * The selectable models, the default, the override and each model's thinking levels
 * (`GET /api/v1/models`). A response that answered 200 is held and served again; a failed or
 * timed-out read answers {@link EMPTY_MODELS} (no models, no override, an older orchestrator)
 * and is not held, so the next call, a send's among them, reads again. A call made while a
 * read is pending waits on it. `fresh` reads again whatever is held, and replaces it only on a
 * 200: a failure then serves the held response, or EMPTY_MODELS with none. The control reads
 * fresh on every opening of its panel.
 */
export function fetchModels({ fresh = false }: { fresh?: boolean } = {}): Promise<ModelsResponse> {
  if (fresh) {
    return readModels().then((answered) => {
      if (answered) {
        modelsPromise = Promise.resolve(answered);
        return answered;
      }
      return modelsPromise ?? EMPTY_MODELS;
    });
  }
  if (!modelsPromise) {
    const read: Promise<ModelsResponse> = readModels().then((answered) => {
      if (answered) return answered;
      if (modelsPromise === read) modelsPromise = null;
      return EMPTY_MODELS;
    });
    modelsPromise = read;
  }
  return modelsPromise;
}

/**
 * The `model` id to send for a turn, read from a /models response, or `undefined`
 * to omit the field (server default). Omits when the org overrides the provider,
 * the choice is the "default" sentinel, or the id isn't in the server's current list.
 */
export function selectableModel(
  modelId: string | undefined,
  { models, override_active }: ModelsResponse,
): string | undefined {
  if (isDefaultModel(modelId) || override_active) return undefined;
  return models.some((m) => m.id === modelId) ? modelId : undefined;
}

/**
 * {@link selectableModel} for a request that reads nothing else of /models: the
 * "default" sentinel is answered without fetching it.
 */
export async function resolveSelectableModel(
  modelId: string | undefined,
): Promise<string | undefined> {
  if (isDefaultModel(modelId)) return undefined;
  return selectableModel(modelId, await fetchModels());
}

// ── Session lifecycle ────────────────────────────────────────────────────────

/**
 * Creates a new orchestrator session and returns its id.
 *
 * The id comes back two ways: the `X-Session-Id` response header AND the
 * `SESSION_STARTED` SSE event in the welcome stream. We prefer the header but
 * fall back to the event, because the header is only visible to JS when the
 * server sets `Access-Control-Expose-Headers` — which the orchestrator's CORS
 * config currently does not, so cross-origin the header reads as null. Reading
 * the event from the body is always reliable.
 */
export async function createSession(
  opts?: { language?: string; model?: string },
  signal?: AbortSignal,
): Promise<string> {
  const res = await authedFetch(url('/sessions'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(opts?.language ? { language: opts.language } : {}),
      ...(opts?.model ? { model: opts.model } : {}),
    }),
    signal,
  });
  await ensureOk(res);

  const headerId = res.headers.get('X-Session-Id');
  if (headerId) {
    void res.body?.cancel(); // don't need the welcome stream
    return headerId;
  }

  // Header hidden (CORS) — pull session_id from the SESSION_STARTED event.
  // readSse owns the reader; returning early from this loop releases it, so we
  // must not also call res.body.cancel() (the stream is already locked).
  for await (const evt of readSse(res, signal)) {
    const code = typeof evt.data.code === 'string' ? evt.data.code : '';
    const id = typeof evt.data.session_id === 'string' ? evt.data.session_id : '';
    if (id && (code === 'SESSION_STARTED' || code === 'SESSION_READY')) {
      return id;
    }
  }
  throw new OrchestratorError('Session id missing from response', 500);
}

/**
 * Frontend Integration Guide §4.3 / §12 `SessionData`: turns are role + markdown text, with the
 * files a user entry carried and the blocks an assistant entry produced.
 */
export interface SessionTranscript {
  session_id: string;
  turn_count: number;
  /**
   * A message a turn took while it ran is a user entry where it entered the reply. Every entry of
   * a record other than the welcome names that record's `turn_id`.
   */
  turns: {
    role?: string;
    content?: unknown;
    stopped?: unknown;
    turn_id?: unknown;
    attachments?: unknown;
    blocks?: unknown;
    block_order?: unknown;
  }[];
  /** A turn holds the session (absent from an orchestrator without the queue). */
  turn_running?: boolean;
  /** The messages waiting for a turn, oldest first; `created_at` is ISO 8601. */
  queue?: { id: string; text: string; created_at: string }[];
  /** The open stop card: a question card, in the session's language. */
  stop_card?: { interaction_id: string; payload: Record<string, unknown> } | null;
  /**
   * The queue is held: the stop card is open, or the last turn was a stop that raised none,
   * until the next turn, or a typed phrase's mode card is open. Absent from an orchestrator
   * that does not say, read as false.
   */
  queue_held?: boolean;
  /**
   * The hold is a typed phrase's mode card's: the orchestrator runs the queue after the card's
   * answer, or a later message closes the card and takes it, so the rows stay queued.
   */
  mode_card_open?: boolean;
  /** The session read a connected account's content: it runs the deployment's model from then on. */
  connected?: boolean;
  /** A compaction holds the session's lock (absent from an orchestrator without compaction). */
  compacting?: boolean;
  /** The last turn the stored compaction folded, null for none (absent likewise). */
  compacted_through_turn?: unknown;
  /**
   * What the next turn's first call carries (`used_tokens`) of the model's input limit
   * (`window_tokens`, null for a model with none), `estimated` until a turn measures it; null
   * while no turn of the session was measured (absent likewise).
   */
  context?: unknown;
}

/** Fetches an existing session's transcript (used to restore history). */
export async function getSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionTranscript> {
  const res = await authedFetch(url(`/sessions/${encodeURIComponent(sessionId)}`), {
    method: 'GET',
    signal,
  });
  await ensureOk(res);
  return (await res.json()) as SessionTranscript;
}

/** One archived turn: the user's message and the reply as markdown text, with their files and blocks. */
export interface ArchivedTurn {
  turn_index: number;
  timestamp?: string;
  user_text?: string;
  assistant_text?: string;
  attachments?: unknown;
  blocks?: unknown;
  block_order?: unknown;
}

export interface ArchivedTranscript {
  session_id: string;
  turns: ArchivedTurn[];
}

/**
 * Fetches a session's archived turns from the durable store. It outlives the
 * live session (`getSession` 404s once the TTL passes, 24 h on the CRM) and
 * keeps the reply that follows a card, but holds only sessions whose history
 * is saved; 404 for any other, and once a session is deleted.
 */
export async function getTranscript(
  sessionId: string,
  signal?: AbortSignal,
): Promise<ArchivedTranscript> {
  const res = await authedFetch(url(`/sessions/${encodeURIComponent(sessionId)}/transcript`), {
    method: 'GET',
    signal,
  });
  await ensureOk(res);
  return (await res.json()) as ArchivedTranscript;
}

/**
 * Erases a session: its archived turns, the recall memory built from it and the
 * live session. 200 also for an id with nothing left; 409 `turn_in_progress`
 * while a turn streams in it.
 */
export async function deleteSession(sessionId: string, signal?: AbortSignal): Promise<void> {
  const res = await authedFetch(url(`/sessions/${encodeURIComponent(sessionId)}`), {
    method: 'DELETE',
    signal,
  });
  await ensureOk(res);
}

/**
 * Erases every saved conversation of the caller and the recall memory built from
 * them (`DELETE /sessions?confirm=all`). 409 `turn_in_progress` while a turn
 * streams in any of them, having erased nothing.
 */
export async function clearSessions(signal?: AbortSignal): Promise<void> {
  const res = await authedFetch(url('/sessions?confirm=all'), { method: 'DELETE', signal });
  await ensureOk(res);
}

// ── File attachments ─────────────────────────────────────────────────────────

export function sanitizeUploadFilename(raw: string): string {
  const cleaned = raw
    .replace(/[\\/:*?"<>|()[\]{}#%&+,;=@`!]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return cleaned || 'upload';
}

export function normalizeOutputName(name: string): string {
  return sanitizeUploadFilename(name.trim()).toLowerCase();
}

/**
 * The orchestrator's own reason for refusing a file (FastAPI's `detail`, a
 * string such as "File type 'application/msword' is not allowed. …" or an
 * object carrying a `code` and a `message`), or `fallback` when the body
 * carries none.
 */
async function refusalDetail(response: Response, fallback: string): Promise<OrchestratorError> {
  const empty = readUploadRefusal(response.status, undefined);
  let refusal = { ...empty, message: fallback };
  try {
    const parsed = readUploadRefusal(response.status, await response.json());
    refusal = parsed.message === empty.message ? { ...parsed, message: fallback } : parsed;
  } catch {
    /* non-JSON error body */
  }
  return new OrchestratorError(refusal.message, refusal.status, refusal.code, refusal.limitBytes);
}

const UPLOAD_UNAVAILABLE = "We couldn't process this file right now. Please try again.";

interface UploadResponse {
  status: number;
  body: string;
}

interface UploadResponseBody {
  detail?: unknown;
  file_ref?: unknown;
}

function uploadAbortError(): DOMException {
  return new DOMException('The upload was cancelled.', 'AbortError');
}

function parseUploadBody(body: string): UploadResponseBody {
  try {
    const parsed: unknown = JSON.parse(body);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function sendUpload(
  form: FormData,
  { signal, onProgress }: UploadOptions,
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(uploadAbortError());
      return;
    }
    const xhr = new XMLHttpRequest();
    const abort = () => {
      xhr.abort();
    };
    const settle = () => {
      signal?.removeEventListener('abort', abort);
    };
    xhr.open('POST', url('/files/upload'));
    for (const [name, value] of Object.entries(authHeaders())) {
      xhr.setRequestHeader(name, value);
    }
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || event.total <= 0) return;
        onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      };
    }
    xhr.onload = () => {
      settle();
      resolve({ status: xhr.status, body: xhr.responseText });
    };
    xhr.onerror = () => {
      settle();
      reject(
        new OrchestratorError(
          "We couldn't upload this file. Check your connection and try again.",
          0,
        ),
      );
    };
    xhr.onabort = () => {
      settle();
      reject(uploadAbortError());
    };
    signal?.addEventListener('abort', abort, { once: true });
    xhr.send(form);
  });
}

export async function uploadFileWithProgress(
  file: File,
  options: UploadOptions = {},
): Promise<string> {
  const form = new FormData();
  const safeName = sanitizeUploadFilename(file.name);
  const type = file.type || mimeTypeForExtension(fileExtension(file.name));
  const safeFile =
    safeName === file.name && type === file.type ? file : new File([file], safeName, { type });
  form.append('file', safeFile, safeName);

  let response = await sendUpload(form, options);
  if (response.status === 401) {
    const refreshed = await refetchToken().then(
      () => true,
      () => false,
    );
    if (refreshed) response = await sendUpload(form, options);
  }

  if (response.status >= 500) throw new OrchestratorError(UPLOAD_UNAVAILABLE, response.status);
  const body = parseUploadBody(response.body);
  if (response.status < 200 || response.status >= 300) {
    const refusal = readUploadRefusal(response.status, body);
    throw new OrchestratorError(refusal.message, refusal.status, refusal.code, refusal.limitBytes);
  }
  if (typeof body.file_ref !== 'string' || !body.file_ref) {
    throw new OrchestratorError(UPLOAD_UNAVAILABLE, 502);
  }
  return body.file_ref;
}

export async function attachFile(
  sessionId: string,
  fileRef: string,
  signal?: AbortSignal,
): Promise<{ fileRef: string; vaultFileId?: string }> {
  const res = await authedFetch(url(`/sessions/${encodeURIComponent(sessionId)}/files`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_ref: fileRef }),
    signal,
  });
  if (!res.ok) {
    throw res.status >= 500
      ? new OrchestratorError(
          "We couldn't attach this file to the chat. Please try again.",
          res.status,
        )
      : await refusalDetail(res, `Failed to attach file: ${res.status.toString()}`);
  }
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  return readAttachResponse(body, fileRef);
}

export async function deleteUploadedFile(fileRef: string, signal?: AbortSignal): Promise<void> {
  const res = await authedFetch(url(`/files/${encodeURIComponent(fileRef)}`), {
    method: 'DELETE',
    signal,
  });
  if (res.status === 404) return;
  await ensureOk(res);
}

// ── Messaging (SSE) ──────────────────────────────────────────────────────────

/** Reply to an open interaction (matches orchestrator InteractionDecisionPayload). */
export interface InteractionDecision {
  intent_id: string;
  flow: string;
  target_id: string;
  decision: string;
  selection?: string;
  text?: string;
}

export interface MessageRequest {
  message?: string;
  /** Answer to an open MCQ/proposal instead of free-text. */
  interaction?: InteractionDecision;
  language?: string;
  /** Selected LLM model id (omit for the server default). See resolveSelectableModel. */
  model?: string;
  /**
   * How much the model reasons: the id of the level the composer shows for the model the request
   * runs on (omit for a model that takes no level, which runs at none), or an older
   * orchestrator's name for it (see levelToSend). A card answer runs at the mode its card was
   * raised under, so it does not send it, except a stop card's "Something else", which carries
   * it as a send does.
   */
  thinking_level?: string;
  /**
   * The turn plans and runs many steps, and can split the work across analysts. A message, a
   * queued send, a drain and a stop card's "Something else" always carry it, false when the
   * switch is off, so the orchestrator's rule for an older caller (a `deep` with no `agentic`
   * runs agentic) applies only to the `deep` an older orchestrator is sent while it is on. Every
   * other card answer carries none: the card's turn runs in the mode it was raised under. A
   * typed phrase that asks for a change ("go agentic", "think hard") raises a mode card, an
   * ordinary question card, before a turn runs with it.
   */
  agentic?: boolean;
  /**
   * CRM navigation/mutation targets the model may choose from. Reserved for the
   * redirection feature; safe to omit until wired.
   */
  client_context?: Record<string, unknown>;
  /**
   * The client queues: a message sent while a turn runs is queued instead of refused with
   * 409, a body with neither a message nor an interaction runs the queue (a drain), and a
   * card answer's turn takes queued messages in. A card answer itself is never queued.
   */
  queue?: boolean;
  /** The message's id in the session's queue: MESSAGE_TAKEN and a removal name it by this. */
  client_message_id?: string;
}

/**
 * What `POST /sessions/{id}/messages` answers in JSON when the request runs no turn: a
 * message queued behind the running turn (202), or refused by the guardrail as it arrived
 * (200, nothing kept); a drain that found a turn running (202), or nothing queued (200).
 */
export type MessageAnswer =
  | { status: 'queued'; message_id: string; position: number }
  | { status: 'refused'; message: string }
  | { status: 'running' }
  | { status: 'empty' };

/**
 * Streams an assistant reply for a message in `sessionId`. Yields raw SSE
 * events; the ChatService adapter maps these to StreamChunks. A request that
 * runs no turn is answered in JSON instead, told apart by its content type: the
 * generator then yields nothing and returns that answer.
 */
export async function* streamMessage(
  sessionId: string,
  body: MessageRequest,
  signal?: AbortSignal,
): AsyncGenerator<SseEvent, MessageAnswer | undefined> {
  const res = await authedFetch(url(`/sessions/${encodeURIComponent(sessionId)}/messages`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  await ensureOk(res);
  if (res.headers.get('Content-Type')?.includes('application/json')) {
    return (await res.json()) as MessageAnswer;
  }
  yield* readSse(res, signal);
  return undefined;
}

/**
 * Asks the session's running turn to stop at its next safe point. True when a
 * turn was running (202 `stopping`), false when none was (200 `not_running`).
 * The turn's stream goes on to the stop card: aborting the fetch that reads it
 * would stop nothing on the server.
 */
export async function stopTurn(sessionId: string, signal?: AbortSignal): Promise<boolean> {
  const res = await authedFetch(url(`/sessions/${encodeURIComponent(sessionId)}/stop`), {
    method: 'POST',
    signal,
  });
  await ensureOk(res);
  const data = (await res.json()) as { status?: string };
  return data.status === 'stopping';
}

/**
 * Removes a message from the session's queue. True when it was removed, false
 * once a turn has taken it (409 `message_taken`).
 */
export async function removeQueued(
  sessionId: string,
  messageId: string,
  signal?: AbortSignal,
): Promise<boolean> {
  const res = await authedFetch(
    url(`/sessions/${encodeURIComponent(sessionId)}/queue/${encodeURIComponent(messageId)}`),
    { method: 'DELETE', signal },
  );
  if (res.status === 409) return false;
  await ensureOk(res);
  return true;
}

// ── Compaction ───────────────────────────────────────────────────────────────

/** A compaction answer's figures: the session's context before and after, and the window. */
export interface CompactFigures {
  /** Null while no turn of the session was measured. */
  beforeTokens: number | null;
  afterTokens: number | null;
  windowTokens: number | null;
  estimated: boolean;
}

/**
 * How `POST /sessions/{id}/compact` answered. `compacted`: a fold was stored, through
 * `throughTurn` (null for a fold that is not a session turn's), and `remainingTurns` more wait
 * for another call; `nothing_to_compact`: nothing to fold. `busy`: the session's lock is held
 * (409 `turn_in_progress`); `not_offered`: 409 `compaction_not_offered` with its reason
 * (`internal_model_off`, `platform`, `connected`); `declined`: 422; `unavailable`: 503, with any
 * reason or none; `failed`: 502 `compaction_failed`; `gone`: 404, the session is gone;
 * `unknown`: any other answer (Cloud Run's 504 among them), or none at all.
 */
export type CompactAnswer =
  | {
      status: 'compacted' | 'nothing_to_compact';
      throughTurn: number | null;
      remainingTurns: number;
      context: CompactFigures | null;
    }
  | { status: 'not_offered'; reason: string }
  | { status: 'busy' | 'declined' | 'unavailable' | 'failed' | 'gone' | 'unknown' };

/** A finite number, else null. */
function figure(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A compaction answer's `context`, or null when it carries none it can read. */
function compactFigures(value: unknown): CompactFigures | null {
  if (!value || typeof value !== 'object') return null;
  const c = value as {
    before_tokens?: unknown;
    after_tokens?: unknown;
    window_tokens?: unknown;
    estimated?: unknown;
  };
  // A window that is not a positive number reads as none, as the session view's does.
  const window = figure(c.window_tokens);
  return {
    beforeTokens: figure(c.before_tokens),
    afterTokens: figure(c.after_tokens),
    windowTokens: window !== null && window > 0 ? window : null,
    estimated: c.estimated === true,
  };
}

/**
 * An error body's `detail`: the compaction route sends `{error, reason}`, the session's lock a
 * plain string, which reads as the error.
 */
function errorDetail(body: unknown): { error?: string; reason?: string } {
  const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
  if (typeof detail === 'string') return { error: detail };
  if (!detail || typeof detail !== 'object') return {};
  const { error, reason } = detail as { error?: unknown; reason?: unknown };
  return {
    ...(typeof error === 'string' ? { error } : {}),
    ...(typeof reason === 'string' ? { reason } : {}),
  };
}

/**
 * Folds the session's older turns into a summary the model reads in their place
 * (`POST /sessions/{id}/compact`), with the `model` the chat's next message would send, none
 * where it sends none. No timeout of its own: an answer can take the orchestrator's whole 300 s.
 * It never throws but for an abort: its error bodies carry `{error, reason}`, which `ensureOk`
 * does not read, and a request that got no answer reads as `unknown`, as Cloud Run's 504 does.
 */
export async function compactSession(
  sessionId: string,
  model: string | undefined,
  signal?: AbortSignal,
): Promise<CompactAnswer> {
  let res: Response;
  let body: unknown;
  try {
    res = await authedFetch(url(`/sessions/${encodeURIComponent(sessionId)}/compact`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(model ? { model } : {}),
      signal,
    });
    body = await res.json().catch(() => undefined);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    return { status: 'unknown' };
  }
  if (res.ok) {
    const answer = (body ?? {}) as {
      status?: unknown;
      through_turn?: unknown;
      remaining_turns?: unknown;
      context?: unknown;
    };
    if (answer.status !== 'compacted' && answer.status !== 'nothing_to_compact') {
      return { status: 'unknown' };
    }
    return {
      status: answer.status,
      throughTurn: figure(answer.through_turn),
      remainingTurns: figure(answer.remaining_turns) ?? 0,
      context: compactFigures(answer.context),
    };
  }
  const { error, reason } = errorDetail(body);
  switch (res.status) {
    case 404:
      return { status: 'gone' };
    case 409:
      if (error === 'turn_in_progress') return { status: 'busy' };
      if (error === 'compaction_not_offered')
        return { status: 'not_offered', reason: reason ?? '' };
      return { status: 'unknown' };
    case 422:
      return { status: error === 'compaction_declined' ? 'declined' : 'unknown' };
    case 502:
      return { status: error === 'compaction_failed' ? 'failed' : 'unknown' };
    case 503:
      return { status: 'unavailable' };
    default:
      return { status: 'unknown' };
  }
}

// ── Chart embed ──────────────────────────────────────────────────────────────

/**
 * A token for trade-system's chart embed (`GET /api/v1/embeds/chart-token`): bound to the
 * one instrument `assetId` names (`AAPL:NASDAQ`), with its expiry and the origin the chart is
 * framed from. A refusal rejects with its status and detail: 429 `rate_limited`, 404
 * `unknown_symbol`, 503 `embed_unavailable`.
 */
export async function fetchChartToken(assetId: string, signal?: AbortSignal): Promise<EmbedToken> {
  const path = `/embeds/chart-token?asset_id=${encodeURIComponent(assetId)}`;
  const res = await authedFetch(url(path), { method: 'GET', signal });
  await ensureOk(res);
  return (await res.json()) as EmbedToken;
}

// ── STT ──────────────────────────────────────────────────────────────────────

/** Transcribes recorded audio to text via the orchestrator (FRONTEND_INTEGRATION §7). */
export async function transcribe(
  audio: Blob,
  language?: string,
  signal?: AbortSignal,
): Promise<string> {
  const ext = audio.type.includes('webm')
    ? 'webm'
    : audio.type.includes('mp4')
      ? 'mp4'
      : audio.type.includes('wav')
        ? 'wav'
        : 'webm';
  const form = new FormData();
  form.append('audio', audio, `voice.${ext}`);
  if (language) form.append('language', language);

  // No Content-Type on purpose — the browser adds the multipart boundary.
  const res = await authedFetch(url('/transcribe'), {
    method: 'POST',
    body: form,
    signal,
  });
  await ensureOk(res);
  const data = (await res.json()) as { text?: string };
  return data.text ?? '';
}
